import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import Photo from './Photo';
import CarouselButton from './CarouselButton';
import type { Photo as PhotoData } from '../types';

type Props = {
  photos: PhotoData[];
  ratio?: string;
  sizes?: string;
  /** Listing card index — drives the №01 tag overlay. */
  index?: number;
  showIndexTag?: boolean;
};

export default function PhotoCarousel({
  photos,
  ratio = '4/3',
  sizes = '(min-width: 768px) 50vw, 100vw',
  index,
  showIndexTag = true,
}: Props) {
  const { t } = useTranslation();
  const [photoIdx, setPhotoIdx] = useState(0);
  const total = photos.length;
  const current = photos[photoIdx];

  const stop = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const next = (e: MouseEvent) => {
    stop(e);
    setPhotoIdx((p) => (p + 1) % total);
  };
  const prev = (e: MouseEvent) => {
    stop(e);
    setPhotoIdx((p) => (p - 1 + total) % total);
  };

  if (!current) return null;

  return (
    <div className="relative h-full w-full">
      <Photo
        photo={current}
        ratio={ratio}
        sizes={sizes}
        fill
        loading={photoIdx === 0 ? 'eager' : 'lazy'}
        priority={photoIdx === 0 && index === 0 ? 'high' : undefined}
      />

      <CarouselButton direction="prev" onClick={prev} ariaLabel={t('carousel.previous')} />
      <CarouselButton direction="next" onClick={next} ariaLabel={t('carousel.next')} />

      <div className="pointer-events-none absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
        {photos.map((p, i) => (
          <span
            key={p.fallback}
            className={`h-1.5 rounded-full transition-all duration-200 ${
              i === photoIdx ? 'w-[18px] bg-white' : 'w-1.5 bg-white/55'
            }`}
          />
        ))}
      </div>

      {showIndexTag && typeof index === 'number' && (
        <div className="pointer-events-none absolute left-3 top-3 rounded-full bg-black/35 px-2.5 py-1 font-mono text-[11px] uppercase tracking-widest text-white">
          №&nbsp;{String(index + 1).padStart(2, '0')}
        </div>
      )}

      <span aria-live="polite" className="sr-only">
        {t('carousel.photoOf', {
          current: photoIdx + 1,
          total,
          caption: current.caption,
        })}
      </span>
    </div>
  );
}
